import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { MDText } from 'i18n-react';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';

import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';
import ellipsis from '@adsk/bim360-matrix-react-helpers/es/ellipsis';
import areTrimmedLinesEqual from '@adsk/bim360-matrix-react-helpers/es/areTrimmedLinesEqual';
import AnimateHeight from '@adsk/bim360-matrix-react-animate-height';
import AnchorButton from '@adsk/bim360-matrix-react-anchor-button';
import linkifyText from '@adsk/bim360-matrix-react-helpers/es/linkifyText';
import parseNewLines from '@adsk/bim360-matrix-react-helpers/es/parseNewLines';
import measureText from '@adsk/bim360-matrix-react-helpers/es/measureText';

import './_SeeMore.scss';

/**
 * Component for showing more / less text in animated way.
 * @param className
 * @param style
 * @param restProps
 * @returns {XML}
 * @constructor
 */
export class SeeMore extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      seeMoreOpen: false,
      isExpanded: false,
      isAnimating: false,
      prefixWidth: 0,
      toggleButtonWidth: 0,
      containerWidth: 0,
      maxWidth: 0,
      ignoreResize: false,
      mainText: '',
      fontSize: null
    };

    this.debouncedRecalcOnResize = debounce(this.recalcOnResize.bind(this), 10);
    this.onAnimationStop = this.onAnimationStop.bind(this);
    this.onClickSeeMore = this.onClickSeeMore.bind(this);
    this.setTextContext = this.setTextContext.bind(this);
    this.getTextWithEllipsis = this.getTextWithEllipsis.bind(this);
    this.setToggleButtonWidth = this.setToggleButtonWidth.bind(this);
    this.setPrefixWidth = this.setPrefixWidth.bind(this);
  }

  componentDidMount() {
    window.requestAnimationFrame(() => {
      this.setState({ mainText: this.getMainText() });
    });

    this.resizeObserver = new ResizeObserver(this.debouncedRecalcOnResize);
    this.resizeObserver.observe(this.containerRef);
  }

  componentWillUnmount() {
    this.resizeObserver.disconnect();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      !this.state.seeMoreOpen &&
      ((this.state.containerWidth && this.state.containerWidth !== prevState.containerWidth) ||
        (this.state.maxWidth && this.state.maxWidth !== prevState.maxWidth))
    ) {
      this.setState({
        containerHeight: null,
        mainText: this.getMainText(),
        ignoreResize: true
      });
    }
  }

  /**
   * recalculates the content of the text when the div is resized
   */

  recalcOnResize(entries) {
    //if the resize was triggered by internal content change, don't recalc again
    if (this.state.ignoreResize) {
      this.setState({ ignoreResize: false });
      return;
    }
    const textContentResizeEvent = entries[0];

    if (
      this.state.isAnimating ||
      (this.state.seeMoreOpen &&
        this.state.containerWidth < textContentResizeEvent.contentRect.width)
    ) {
      return;
    }
    this.setState({
      containerWidth: textContentResizeEvent.contentRect.width,
      maxWidth: textContentResizeEvent.contentRect.width
    });
  }

  /**
   * return the trimmed text with the ellipsis
   * it breaks down each line in the original text to count the number of actual lines it takes in the container, and returns the text that fits
   * in the container area
   */

  getTextWithEllipsis() {
    const { containerWidth, prefixWidth, maxWidth, toggleButtonWidth, fontSize } = this.state;
    const { numberOfAllowedLines } = this.props;

    const allowedWidth = maxWidth ? Math.min(maxWidth, containerWidth) : containerWidth;
    const lines = this.props.text.split('\n');
    const text = [];

    for (
      let linesRemaining = numberOfAllowedLines, lineIndex = 0;
      lineIndex < lines.length && linesRemaining > 0;
      lineIndex++
    ) {
      const lineWidthBase = measureText(lines[lineIndex], fontSize);
      const lineWidth = lineIndex === 0 ? lineWidthBase + prefixWidth : lineWidthBase;
      const wrappedTextLineCount = Math.ceil(lineWidth / allowedWidth);
      text[lineIndex] =
        wrappedTextLineCount < linesRemaining
          ? lines[lineIndex]
          : ellipsis(
            lines[lineIndex],
            0,
            linesRemaining * containerWidth - toggleButtonWidth - 5,
            fontSize
          );
      linesRemaining -= wrappedTextLineCount;
    }

    return text.join('\n');
  }

  getMainText() {
    return this.state.seeMoreOpen ? this.props.text : this.getTextWithEllipsis();
  }

  setContainerStyle(containerRef) {
    if (!this.containerRef) {
      this.containerRef = containerRef;
      if (!this.state.maxWidth) {
        const containerWidth = containerRef.clientWidth;
        this.setState({ containerWidth, maxWidth: containerWidth });
      }
    }
    if (this.state.mainText && !this.state.containerHeight) {
      const containerHeight =
        containerRef && parseInt(window.getComputedStyle(containerRef).height, 10);
      containerRef && containerHeight && this.setState({ containerHeight });
    }
  }

  setPrefixWidth(prefixRef) {
    if (!this.prefixRef) {
      this.prefixRef = prefixRef;
      prefixRef && this.setState({ prefixWidth: prefixRef.offsetWidth });
    }
  }

  setToggleButtonWidth(toggleButton) {
    if (!toggleButton || toggleButton.offsetWidth === this.state.toggleButtonWidth) {
      return;
    }
    this.setState({ toggleButtonWidth: toggleButton.offsetWidth });
  }

  wrapWithSpan(text) {
    return <span dangerouslySetInnerHTML={{ __html: linkifyText(parseNewLines(text)) }} />;
  }

  onAnimationStop(...args) {
    this.setState({ isAnimating: false, mainText: this.getMainText() });
    this.props.onAnimationEnd(this.state.isExpanded, ...args);
  }

  onClickSeeMore(e) {
    const clickSetStateCallback = () => {
      const changedObj = {};
      if (this.state.seeMoreOpen) {
        changedObj.mainText = this.getMainText();
      }
      this.setState(changedObj, () => {
        this.setState({ isExpanded: this.state.seeMoreOpen, isAnimating: true });
      });
    };
    this.setState({ seeMoreOpen: !this.state.seeMoreOpen }, clickSetStateCallback);

    this.props.onToggleClick(e);
  }

  setTextContext(textContent) {
    if (textContent && !this.state.fontSize) {
      this.setState({ fontSize: parseFloat(window.getComputedStyle(textContent).fontSize) });
    }
  }

  renderContent() {
    const { maxWidth } = this.state;
    const seeMoreButton = (
      <AnchorButton
        className={classNames('SeeMore', this.props.classNames.toggleButtonText)}
        style={this.props.styles.toggleButtonText}
        onClick={this.onClickSeeMore}
      >
        <span
          className="SeeMore__toggle"
          data-testid={`SeeMore__toggle--${this.state.seeMoreOpen ? 'less' : 'more'}`}
          ref={this.setToggleButtonWidth}
        >
          {this.state.seeMoreOpen
            ? this.props.toggleButtonText.less || this.props[TSymbolProp].translate('SEE_LESS')
            : this.props.toggleButtonText.more || this.props[TSymbolProp].translate('SEE_MORE')}
        </span>
      </AnchorButton>
    );
    const isTextFullyShown = areTrimmedLinesEqual(this.getTextWithEllipsis(), this.props.text);

    return (
      <div ref={this.setTextContext}>
        {this.props.textPrefix && (
          <span
            style={this.props.styles.textPrefix}
            className={classNames('SeeMore', this.props.classNames.textPrefix)}
            ref={this.setPrefixWidth}
          >{`${this.props.textPrefix} `}</span>
        )}
        {this.wrapWithSpan(isTextFullyShown || !maxWidth ? this.props.text : this.state.mainText)}
        {!isTextFullyShown && this.state.seeMoreOpen ? <br /> : ' '}
        {!isTextFullyShown && seeMoreButton}
      </div>
    );
  }

  render() {
    const height = this.state.containerHeight || (this.props.styles.mainContent || {}).height;
    return (
      <div
        style={this.props.styles.mainContent}
        ref={r => {
          this.setContainerStyle(r);
        }}
        className={classNames('SeeMore', this.props.classNames.mainContent)}
      >
        <AnimateHeight
          isOpen={this.state.isExpanded}
          timeout={this.props.animationDuration}
          height={`${height}px`}
          onEntered={this.onAnimationStop}
          onExited={this.onAnimationStop}
        >
          {this.renderContent()}
        </AnimateHeight>
      </div>
    );
  }
}

SeeMore.defaultProps = {
  numberOfAllowedLines: 2,
  animationDuration: 500,
  styles: {},
  classNames: {},
  toggleButtonText: {},
  onAnimationEnd: noop,
  onToggleClick: noop
};

SeeMore.propTypes = {
  /**
   * Class to add to the main blocks of the component
   */
  classNames: PropTypes.shape({
    /**
     * Custom class for the main content node
     */
    mainContent: PropTypes.string,
    /**
     * Custom class for the textPrefix content node
     */
    textPrefix: PropTypes.string,
    /**
     * Custom class for the toggleButtonText content node
     */
    toggleButtonText: PropTypes.string
  }),
  /**
   * Custom styles
   */
  styles: PropTypes.shape({
    /**
     * * Custom style for the mainContent content node
     */
    mainContent: PropTypes.object,
    /**
     * Custom style for the textPrefix content node
     */
    textPrefix: PropTypes.object,
    /**
     * * Custom style for the toggleButtonText content node
     */
    toggleButtonText: PropTypes.object
  }),
  /**
   * Optional duration of the animation. Defaults to 500
   */
  animationDuration: PropTypes.number,
  /**
   * The text to shown.
   */
  text: PropTypes.string.isRequired,

  /**
   * Prefix text to put with a different style
   */
  textPrefix: PropTypes.string,

  toggleButtonText: PropTypes.shape({
    /**
     * The text for seeing more of the content
     */
    more: PropTypes.string,
    /**
     * The text for seeing less of the content
     */
    less: PropTypes.string
  }),
  /**
   * The number of lines of text to show before pressing the toggle button.
   * NOTICE - There are cases when the number of lines will be numberOfAllowedLines+1 due to the use of ellipsis and parsing new lines together.
   */
  numberOfAllowedLines: PropTypes.number,
  /**
   * The locale to use for this component
   */
  locale: PropTypes.string,
  /**
   * A function to be invoked when the animation ends
   * Output params: (isExapnded, ...args)
   * isExapnded - whether the full SeeMore text is shown
   * (...args) are the arguments returned by Bootstrap's Transition onExited/onEntered callbacks and simply passed on in this chain
   */
  onAnimationEnd: PropTypes.func,
  /**
   * A function invoked on See More/See Less button click. Receives the click event as a parameter.
   */
  onToggleClick: PropTypes.func
};

export default withLocale(SeeMore, ({ locale }) => new MDText(require(`./i18n/${locale}.json`)));
